import { useState, useRef, useEffect } from 'react';
import { QrCode, X, Camera } from 'lucide-react';
import QrScanner from 'qr-scanner';

interface QRScannerProps {
  onScan: (result: string) => void;
  /** Optional station label shown in the idle black scanner card */
  stationName?: string;
}

export function QRScanner({ onScan, stationName }: QRScannerProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);

  const startScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);

      // Wait for the video element to be rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      // Check if camera is available
      const hasCamera = await QrScanner.hasCamera();
      if (!hasCamera) {
        throw new Error('No camera found on this device');
      }

      // Create QR scanner instance
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          onScan(result.data);
          stopScanning();
        },
        {
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment', // Use back camera on mobile
        }
      );

      await qrScannerRef.current.start();
    } catch (err) {
      console.error('Error starting QR scanner:', err);
      setError(err instanceof Error ? err.message : 'Failed to start camera');
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setIsScanning(false);
    setError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  // Start scanning when modal opens and video element is available
  useEffect(() => {
    if (isScanning && videoRef.current && !qrScannerRef.current) {
      const initializeScanner = async () => {
        try {
          // Check if camera is available
          const hasCamera = await QrScanner.hasCamera();
          if (!hasCamera) {
            throw new Error('No camera found on this device');
          }

          // Create QR scanner instance
          qrScannerRef.current = new QrScanner(
            videoRef.current!,
            (result) => {
              onScan(result.data);
              stopScanning();
            },
            {
              highlightScanRegion: true,
              highlightCodeOutline: true,
              preferredCamera: 'environment',
            }
          );

          await qrScannerRef.current.start();
        } catch (err) {
          console.error('Error initializing QR scanner:', err);
          setError(err instanceof Error ? err.message : 'Failed to start camera');
        }
      };

      initializeScanner();
    }
  }, [isScanning, onScan]);

  const handleStartScanning = () => {
    setError(null);
    setIsScanning(true);
  };

  return (
    <div className="relative w-full">
      {!isScanning ? (
        <button
          onClick={handleStartScanning}
          className="w-full h-40 bg-black border-2 border-dashed border-sw-border flex flex-col items-center justify-center relative overflow-hidden hover:border-sw-yellow transition-colors"
          title="Scan QR Code"
        >
          {stationName && (
            <div
              className="absolute inset-0 flex items-center justify-center px-6 pointer-events-none z-0"
              aria-hidden
            >
              <span className="text-[44px] font-black uppercase tracking-[0.04em] text-white/25 text-center leading-tight line-clamp-2 font-brutal">
                {stationName}
              </span>
            </div>
          )}
          <div className="w-[120px] h-[120px] border-2 border-sw-yellow opacity-90 relative z-10" />
          <QrCode className="w-8 h-8 text-sw-yellow absolute z-10" />
        </button>
      ) : (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-sw-panel border-2 border-sw-yellow p-4 max-w-sm w-full mx-4 font-brutal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-black uppercase text-white tracking-[0.02em]">Scan QR Code</h3>
              <button
                onClick={stopScanning}
                className="p-1 text-sw-muted hover:text-sw-yellow"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error ? (
              <div className="text-center py-8">
                <Camera className="w-12 h-12 text-sw-muted mx-auto mb-4" />
                <p className="pen-error !rounded-none mb-4">{error}</p>
                <button
                  onClick={handleStartScanning}
                  className="pen-btn-primary !rounded-none font-brutal font-black"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="relative">
                <video
                  ref={videoRef}
                  className="w-full h-48 bg-black object-cover border-2 border-sw-yellow"
                  playsInline
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-36 h-36 border-2 border-sw-yellow"></div>
                </div>
                <p className="text-center text-sw-muted mt-4 text-[10px] uppercase tracking-[0.1em] font-extrabold font-brutal">
                  Position the QR code within the frame to scan
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}